import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ILesson } from '@gray/domain/models/lesson/lesson.model';
import { IPlanWeek } from '@gray/domain/models/plan/plan-week.model';
import { IPlanWeekItem } from '@gray/domain/models/plan/plan-week-item.model';
import { IPlannerConfig } from '@gray/ui/planner/planner.model';
import { IPlannerAssignEvent } from '@gray/ui/planner/planner-day-board/planner-day-board.component';
import { CoachStudentPlanService } from '../../../core/services/coach-student-plan.service';
import { CoachCatalogService } from '../../../core/services/coach-catalog.service';
import { CoachPeriodService } from '../../../core/services/coach-period.service';
import { PlannerItemModalComponent } from './planner-item-modal/planner-item-modal.component';
import { PlannerTransferModalComponent } from './planner-transfer-modal/planner-transfer-modal.component';
import { PlannerPlanModalComponent } from './planner-plan-modal/planner-plan-modal.component';
import { PlannerDeletePlanModalComponent } from './planner-delete-plan-modal/planner-delete-plan-modal.component';

/**
 * The student's weekly planner as the coach edits it.
 *
 * The board itself is the shared one; this component only decides which week is shown, which
 * endpoint an action hits and which dialog opens — the coach acts on the plan exactly as the
 * student would.
 */
@Component({
  selector: 'coach-planner-tab',
  templateUrl: './planner-tab.component.html',
  styleUrl: './planner-tab.component.scss',
})
export class PlannerTabComponent implements OnChanges, OnDestroy {
  @Input() studentId!: number;
  @Input() studentName?: string | null;
  /** Colours the column headers with the student's own level, as their planner does. */
  @Input() levelKey?: string | null;

  weeks: IPlanWeek[] = [];
  selectedWeek?: IPlanWeek;
  items: IPlanWeekItem[] = [];
  lessons: ILesson[] = [];

  /** A coach may do everything the student may. */
  config: IPlannerConfig = {canEdit: true, canAddTopic: true, showAssignedTime: true};

  /** Which layout the board is in, the same two the student planner offers. */
  mode: 'lessons' | 'days' = 'lessons';

  weeksIsLoading = false;
  itemsIsLoading = false;

  destroyed$ = new Subject();

  constructor(
    private planService: CoachStudentPlanService,
    private catalogService: CoachCatalogService,
    private periodService: CoachPeriodService,
    private modalService: NgbModal,
    private toastrService: ToastrService,
    private translate: TranslateService,
  ) {
  }

  ngOnChanges(): void {
    if (this.studentId) {
      this.getLessons();
      this.getWeeks();
    }
  }

  /**
   * The columns are the lesson catalog, not whatever the week happens to contain — an empty week
   * still has to show the board so the coach can add to it.
   */
  getLessons() {
    this.catalogService.getLessons()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        this.lessons = (r.data?.result ?? r.data ?? []) as ILesson[];
      });
  }

  periodId(): number | null {
    return this.periodService.selectedId;
  }

  /** Completed of total for the selected week — the counter the tab bar shows. */
  progressLabel(): string | null {
    if (!this.items.length) {
      return null;
    }

    const completed = this.items.filter(item => !!item.completedAt).length;
    return `${completed}/${this.items.length}`;
  }

  getWeeks() {
    if (!this.periodId()) {
      return;
    }

    this.weeksIsLoading = true;
    this.planService.getWeeks(this.studentId, this.periodId())
      .pipe(takeUntil(this.destroyed$), finalize(() => this.weeksIsLoading = false))
      .subscribe(r => {
        this.weeks = (r.data ?? []) as IPlanWeek[];
        // The week in progress is the one a coach wants to see first.
        const active = this.weeks.find(week => week.isActive) ?? this.weeks[0];
        this.selectWeek(active);
      });
  }

  selectWeek(week?: IPlanWeek) {
    if (!week) {
      return;
    }

    this.selectedWeek = week;
    this.getItems();
  }

  /**
   * `silent` refreshes leave the board on screen. Only opening a week loads behind a skeleton;
   * ticking an item or dropping it on a day must not collapse the whole plan and shift the page.
   */
  getItems(silent = false) {
    if (!this.selectedWeek) {
      return;
    }

    this.itemsIsLoading = !silent;
    this.planService.getWeekItems(this.studentId, this.selectedWeek.id, this.periodId())
      .pipe(takeUntil(this.destroyed$), finalize(() => this.itemsIsLoading = false))
      .subscribe(r => {
        this.items = (r.data?.items ?? r.data ?? []) as IPlanWeekItem[];
      });
  }

  /** Boards rebuild on an input change, so a local edit has to hand them a new array. */
  private refreshItems() {
    this.items = [...this.items];
  }

  // region Item actions
  toggleItem(item: IPlanWeekItem) {
    if (!this.selectedWeek) {
      return;
    }

    const wasCompleted = !!item.completedAt;
    // Answer the click straight away; the reload only reconciles what the API decided.
    item.completedAt = wasCompleted ? null : new Date();
    this.refreshItems();

    const request$ = wasCompleted
      ? this.planService.uncompleteWeekItem(this.studentId, this.selectedWeek.id, item.id, this.periodId())
      : this.planService.completeWeekItem(this.studentId, this.selectedWeek.id, item.id, this.periodId());

    request$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => this.getItems(true),
      error: () => {
        item.completedAt = wasCompleted ? new Date() : null;
        this.refreshItems();
      },
    });
  }

  duplicateItem(item: IPlanWeekItem) {
    this.planService.copyWeekItem(this.studentId, item.id, {}, this.periodId())
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.toastrService.success(this.translate.instant('planner.item_duplicated'));
        this.getItems(true);
      });
  }

  removeItem(item: IPlanWeekItem) {
    this.planService.deleteWeekItem(this.studentId, item.id, this.periodId())
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.toastrService.success(this.translate.instant('planner.item_removed'));
        this.items = this.items.filter(current => current.id !== item.id);
        this.getItems(true);
      });
  }

  openTransfer(item: IPlanWeekItem) {
    if (!this.selectedWeek) {
      return;
    }

    const modalRef = this.modalService.open(PlannerTransferModalComponent, {size: 'md', centered: true});
    modalRef.componentInstance.weeks = this.weeks.filter(week => week.id !== this.selectedWeek?.id);
    modalRef.componentInstance.item = item;

    modalRef.closed.pipe(takeUntil(this.destroyed$)).subscribe((toWeekId: number) => {
      this.planService
        .transferWeekItem(this.studentId, this.selectedWeek.id, item.id, toWeekId, this.periodId())
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          this.toastrService.success(this.translate.instant('planner.item_transferred'));
          this.getItems();
        });
    });
  }

  setMode(mode: 'lessons' | 'days') {
    this.mode = mode;
  }

  /** Loads a ready-made plan onto the student's weeks. */
  openSelectPlan() {
    if (!this.periodId()) {
      return;
    }

    const modalRef = this.modalService.open(PlannerPlanModalComponent, {size: 'md', centered: true, scrollable: true});
    modalRef.componentInstance.studentId = this.studentId;
    modalRef.componentInstance.periodId = this.periodId();
    modalRef.componentInstance.weeks = this.weeks;

    modalRef.closed.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.toastrService.success(this.translate.instant('planner.plan_imported'));
      this.getItems(true);
    });
  }

  openDeletePlan() {
    if (!this.periodId()) {
      return;
    }

    const modalRef = this.modalService.open(PlannerDeletePlanModalComponent, {size: 'md', centered: true});
    modalRef.componentInstance.studentId = this.studentId;
    modalRef.componentInstance.periodId = this.periodId();

    modalRef.closed.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.toastrService.success(this.translate.instant('planner.plan_removed'));
      this.getItems(true);
    });
  }

  /**
   * Dropping an item on a day schedules it; dropping it back on the strip clears the day again.
   * The board reports the move, the plan is what decides it happened.
   */
  onItemAssigned(event: IPlannerAssignEvent) {
    if (!this.selectedWeek) {
      return;
    }

    // The card has to land where it was dropped before the request comes back.
    const previousDate = event.item.assignedDate;
    event.item.assignedDate = event.date;
    this.refreshItems();

    // A calendar day, not an instant: an ISO timestamp would be read a day earlier or later
    // depending on where the server stands.
    const request$ = event.date
      ? this.planService.assignItemToDay(
        this.studentId,
        this.selectedWeek.id,
        event.item.id,
        {assignedDate: event.date.toISOString().slice(0, 10)},
        this.periodId(),
      )
      : this.planService.deleteAssignment(this.studentId, this.selectedWeek.id, event.item.id, this.periodId());

    request$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => this.getItems(true),
      error: () => {
        event.item.assignedDate = previousDate;
        this.refreshItems();
      },
    });
  }

  openAddTopic(lesson?: ILesson) {
    if (!this.selectedWeek || !this.periodId()) {
      return;
    }

    const modalRef = this.modalService.open(PlannerItemModalComponent, {size: 'md', centered: true});
    modalRef.componentInstance.studentId = this.studentId;
    modalRef.componentInstance.periodId = this.periodId();
    modalRef.componentInstance.weekId = this.selectedWeek.id;
    modalRef.componentInstance.lesson = lesson;

    modalRef.closed.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.toastrService.success(this.translate.instant('planner.item_created'));
      this.getItems(true);
    });
  }
  // endregion

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
